/**
 * Turn an API error into text that is safe to render.
 *
 * FastAPI sends `detail` as a string for HTTPException, but as an array of
 * `{loc, msg, type}` objects for a 422 validation error. Rendering that array
 * straight into JSX crashes React, so every error message shown to the user
 * should go through apiErrorText.
 */

/** Request parts FastAPI puts first in `loc`; they mean nothing to a user. */
const LOC_ROOTS = ['body', 'query', 'path', 'header', 'cookie'];

/** Pydantic prefixes messages from custom validators with this. */
const VALUE_ERROR_PREFIX = /^(value|assertion) error,\s*/i;

/** "locations row 3 ChannelNo" from ['body', 'locations', 2, 'ChannelNo'].
 *  List indexes become 1-based row numbers. */
const describeLoc = (loc: unknown): string => {
  if (!Array.isArray(loc)) return '';
  const parts = loc.slice(LOC_ROOTS.includes(String(loc[0])) ? 1 : 0);
  return parts
    .map((p) => (typeof p === 'number' ? `row ${p + 1}` : String(p)))
    .join(' ');
};

const describeItem = (item: any): string => {
  if (typeof item === 'string') return item.trim();
  if (!item || typeof item !== 'object') return '';
  const raw = typeof item.msg === 'string' ? item.msg : typeof item.message === 'string' ? item.message : '';
  const msg = raw.replace(VALUE_ERROR_PREFIX, '').trim();
  if (!msg) return '';
  const where = describeLoc(item.loc);
  return where ? `${where}: ${msg}` : msg;
};

export const apiErrorText = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map(describeItem).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  } else if (detail && typeof detail === 'object') {
    const msg = describeItem(detail);
    if (msg) return msg;
  }
  if (typeof err?.message === 'string' && err.message.trim()) return err.message;
  return fallback;
};

export default apiErrorText;
